import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { InnerHTML } from "../../../../components/inner-html";
import { GridTable } from "../../../../components/grid-table";
import { messages } from "./messages";
import {
  CancellationPolicyEntity,
  CancellationPolicyConfigurationType,
  RawCancellationPolicyConfiguration,
  PenaltyBracket,
} from "./domain/cancellationPolicy.entity";

interface CancellationPoliciesProps {
  formData: {
    checkIn: string;
    checkOut: string;
    condition: {
      cancellation?: string;
      entry?: string;
      exit?: string;
      pets?: string;
      children?: string;
      childrenPolicy?: string;
      groups?: string;
      additionalInfo?: string;
    };
    cancellationPolicy: {
      description?: string;
      configurations?: RawCancellationPolicyConfiguration[];
    };
    totalAmount?: number;
    dailyValues?: Array<{ amount: number; ticker: string }>;
    cancellationLastDay: string;
  };
}

const getRowRange = (
  bracket: PenaltyBracket,
  startDate: string,
  startHour: string,
  endDate: string,
  endHour: string,
  intl: any
): string => {
  if (bracket.startDate && bracket.endDate) {
    return `${startDate} ${startHour} - ${endDate} ${endHour}`.trim();
  } else if (bracket.startDate) {
    return `${intl.formatMessage(messages.from)} ${startDate} ${startHour}`.trim();
  } else if (bracket.endDate) {
    return `${intl.formatMessage(messages.until)} ${endDate} ${endHour}`.trim();
  }
  return "";
};

export function CancellationPolicies({ formData }: CancellationPoliciesProps) {
  const intl = useIntl();

  const penaltyBrackets = useMemo(() => {
    if (!formData.cancellationPolicy.configurations || !formData.checkIn || !formData.checkOut) {
      return [];
    }

    const checkIn = new Date(formData.checkIn);
    const checkOut = new Date(formData.checkOut);
    const cutOffDate = new Date(formData.cancellationLastDay);
    const currency = "EUR";

    const firstNightCost = {
      amount: formData.dailyValues?.[0]?.amount || 0,
      currency,
    };

    const totalRate = {
      amount: formData.totalAmount || 0,
      currency,
    };

    const cancellationPolicyEntity = new CancellationPolicyEntity(
      checkIn,
      checkOut,
      cutOffDate,
      currency,
      firstNightCost,
      totalRate,
      formData.cancellationPolicy.configurations
    );

    return cancellationPolicyEntity.getPenaltyBrackets();
  }, [
    formData.cancellationPolicy.configurations,
    formData.checkIn,
    formData.checkOut,
    formData.totalAmount,
    formData.dailyValues,
    formData.cancellationLastDay,
  ]);

  const mappedGrid = useMemo(() => {
    return penaltyBrackets.map(bracket => {
      const formatDate = (date?: Date) => {
        if (!date) return "";
        return date.toLocaleDateString();
      };

      const startDate = bracket.startDate ? formatDate(bracket.startDate) : "";
      const startHour = bracket.startHour ? `${bracket.startHour}h*` : "";
      const endDate = bracket.endDate ? formatDate(bracket.endDate) : "";
      const endHour = bracket.endHour ? `${bracket.endHour}h*` : "";

      const dateRange = getRowRange(bracket, startDate, startHour, endDate, endHour, intl);

      const description =
        bracket.type === CancellationPolicyConfigurationType.CHARGE_FIRST_NIGHT
          ? ` (${intl.formatMessage(messages.firstNight)})`
          : bracket.type === CancellationPolicyConfigurationType.PERCENTAGE
          ? ` (${bracket.penaltyBase}%)`
          : "";

      const penaltyText = `${bracket.penalty.currency} ${intl.formatNumber(
        bracket.penalty.amount
      )}${description}`;

      return {
        cellOne: dateRange,
        cellTwo: penaltyText,
        className: bracket.penalty.amount === 0 ? "text-primary-100 !font-bold" : "",
      };
    });
  }, [penaltyBrackets, intl]);
  return (
    <div className="flex flex-col text-left items-center h-full">
      {formData.condition.cancellation ? (
        <div className="text-[12px] pt-3 pb-2.5 w-full !leading-4">
          <InnerHTML html={formData.condition.cancellation} />
        </div>
      ) : null}
      {penaltyBrackets.length ? (
        <>
          <div className="text-[15px] font-bold pt-3 pb-2.5 w-full">
            <FormattedMessage {...messages.cancellationRestriction} />
          </div>
          <div className="text-[15px] py-2.5 w-full">
            <GridTable
              gridHeaders={[
                intl.formatMessage(messages.dates),
                intl.formatMessage(messages.penalties),
              ]}
              gridContent={mappedGrid}
            />
          </div>
          <div className="text-[12px] text-gray-400 w-full flex justify-between">
            <span>{`*${intl.formatMessage(messages.localHour)}`}</span>
            <span></span>
          </div>
        </>
      ) : null}

      {formData.cancellationPolicy.description ? (
        <>
          <div className="text-[15px] font-bold pt-3 pb-2.5 w-full">
            <FormattedMessage {...messages.cancellationPolicies} />
          </div>
          <div className="text-[12px] pt-3 pb-2.5 w-full !leading-4">
            <InnerHTML html={formData.cancellationPolicy.description} />
          </div>
        </>
      ) : null}

      {formData.condition.entry || formData.condition.exit ? (
        <div className="flex flex-col pt-3 w-full">
          <div className="text-[15px] font-bold pb-2">
            <FormattedMessage {...messages.schedule} />
          </div>
          {formData.condition.entry && (
            <div className="flex text-[12px] pb-1.5">
              <span className="pr-1">
                <FormattedMessage {...messages.entry} />:
              </span>
              <InnerHTML html={formData.condition.entry} />
            </div>
          )}
          {formData.condition.exit && (
            <div className="flex text-[12px] w-full">
              <>
                <span className="pr-1">
                  <FormattedMessage {...messages.exit} />:
                </span>
                <InnerHTML html={formData.condition.exit} />
              </>
            </div>
          )}
        </div>
      ) : null}

      {formData.condition.pets && (
        <div className="flex flex-col pt-3 w-full">
          <div className="text-[15px] font-bold">
            <FormattedMessage {...messages.pets} />
          </div>
          <InnerHTML className="text-[12px] !leading-4" html={formData.condition.pets} />
        </div>
      )}
      {formData.condition.children && (
        <div className="flex flex-col pt-3 w-full">
          <div className="text-[15px] font-bold">
            <FormattedMessage {...messages.childrenAndBeds} />
          </div>
          <InnerHTML
            as="div"
            className="text-[12px] !leading-4"
            html={formData.condition.childrenPolicy || ""}
          />
        </div>
      )}
      {formData.condition.groups && (
        <div className="flex flex-col pt-3 w-full">
          <div className="text-[15px] font-bold">
            <FormattedMessage {...messages.groups} />
          </div>
          <InnerHTML as="div" className="text-[12px] !leading-4" html={formData.condition.groups} />
        </div>
      )}
      {formData.condition.additionalInfo && (
        <div className="flex flex-col pt-3 w-full">
          <div className="text-[15px] font-bold !leading-4">
            <FormattedMessage {...messages.additionalInfo} />
          </div>
          <InnerHTML as="div" className="text-[12px]" html={formData.condition.additionalInfo} />
        </div>
      )}
    </div>
  );
}
